import React, { useEffect, useRef, useState } from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import classNames from 'classnames';

import Button from 'src/component/UI/Button';
import LoadingSpinner from 'src/component/UI/LoadingSpinner';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';
import './ExportMenu.scss';

export type ExportOption = {
  key: string;
  label: string;
  icon?: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  onClick: () => void;
};

type ExportMenuProps = {
  options: ExportOption[];
  triggerLabel?: string;
  triggerIcon?: React.ReactNode;
  triggerClassName?: string;
  disabled?: boolean;
};

export default function ExportMenu ({
  options,
  triggerLabel,
  triggerIcon,
  triggerClassName,
  disabled = false,
}: ExportMenuProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const visibleOptions = options.filter((o) => !o.hidden);
  const [primaryKey, setPrimaryKey] = useState<string | null>(
    visibleOptions[0]?.key ?? null,
  );

  // Keep primaryKey pointing at a visible option. If the current primary
  // disappears (e.g. PDF services get turned off), fall back to the first
  // visible option. Also seed the primary when the first visible option
  // becomes available after an initial empty state.
  const visibilityKey = options.map((o) => `${o.key}:${o.hidden ? '0' : '1'}`).join(',');
  useEffect(() => {
    const stillVisible = primaryKey && visibleOptions.some((o) => o.key === primaryKey);
    if (!stillVisible) {
      setPrimaryKey(visibleOptions[0]?.key ?? null);
    }
  }, [visibilityKey]);

  const primary = visibleOptions.find((o) => o.key === primaryKey) ?? visibleOptions[0] ?? null;
  const hasOptions = visibleOptions.length > 0;

  const runPrimary = () => {
    if (!primary || primary.disabled || primary.loading) return;
    primary.onClick();
  };

  const handleSelect = (option: ExportOption) => {
    setOpen(false);
    setPrimaryKey(option.key);
    if (option.disabled || option.loading) return;
    option.onClick();
  };

  const primaryIcon = (triggerIcon ?? <FileDownloadIcon className='export-menu-trigger-icon' />) as React.ReactElement;

  const baseLabel = triggerLabel ?? t('export.export');
  const primaryLabel = primary ? `${baseLabel} (${primary.key.toUpperCase()})` : baseLabel;

  return (
    <div ref={containerRef} className={classNames('export-menu-split', triggerClassName)}>
      <Button
        border
        className='export-menu-primary'
        onClick={runPrimary}
        disabled={disabled || !hasOptions || !!primary?.disabled}
        loading={!!primary?.loading}
        icon={primaryIcon}
        spinner={<LoadingSpinner color={variables.primary} size={16} />}
      >
        <span className='export-menu-primary-text'>{primaryLabel}</span>
      </Button>
      <Button
        border
        className='export-menu-caret'
        ariaLabel={t('export.export')}
        disabled={disabled || !hasOptions}
        icon={<KeyboardArrowDownIcon className='export-menu-caret-icon' />}
        onClick={() => setOpen((o) => !o)}
      />
      <Menu
        anchorEl={containerRef.current}
        open={open && hasOptions}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            style: { width: containerRef.current?.offsetWidth },
          },
        }}
      >
        {visibleOptions.map((option) => {
          const isPrimary = option.key === primaryKey;
          const isDisabled = option.disabled || option.loading;
          return (
            <MenuItem
              key={option.key}
              disabled={isDisabled}
              onClick={() => handleSelect(option)}
              className='export-menu-item'
            >
              <ListItemIcon className='export-menu-item-icon'>
                {option.icon ?? <FileDownloadIcon fontSize='small' />}
              </ListItemIcon>
              <ListItemText primary={option.label} />
              <span className='export-menu-item-check'>
                {option.loading ? (
                  <LoadingSpinner color={variables.primary} size={16} />
                ) : isPrimary ? (
                  <CheckIcon fontSize='large' className='export-menu-item-check-icon' />
                ) : null}
              </span>
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
}
